/**
 * Tenant-admin Customers page.
 *
 * Layout:
 *   ┌─────────────────────────┬────────────────────────────┐
 *   │ Customer list           │ Detail drawer              │
 *   │ (filterable, sortable)  │ (profile + tabs:           │
 *   │                         │  Orders/Addresses/Wallet/  │
 *   │                         │  Coins) — only when a row  │
 *   │                         │  is selected.              │
 *   └─────────────────────────┴────────────────────────────┘
 *
 * Information design follows the Swiggy/Zomato admin pattern:
 *   • Lifetime value (total spent) is the lead metric — it's how
 *     ops triages who matters.
 *   • Mobile + name are equally weighted (mobile is the unique key).
 *   • Last-order recency + status colour-coded badges.
 *   • Wallet ₹ and Coins shown inline so support agents can resolve
 *     "I have credits!" disputes without opening the drawer.
 *   • Block / Unblock is one click and snaps back instantly.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, ShieldCheck, ShieldOff, X, MapPin, IndianRupee, Coins as CoinsIcon, Wallet as WalletIcon, ShoppingBag, Phone, Mail, Calendar, RefreshCw, Merge } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";

const STATUS_COLOURS = {
    ACTIVE: "bg-emerald-100 text-emerald-700",
    BLOCKED: "bg-red-100 text-red-700",
    DELETED: "bg-zinc-200 text-zinc-600",
};

const ORDER_STATUS_COLOURS = {
    DELIVERED: "bg-emerald-100 text-emerald-700",
    CANCELLED: "bg-red-100 text-red-700",
    REJECTED: "bg-red-100 text-red-700",
    PLACED: "bg-amber-100 text-amber-700",
    CONFIRMED: "bg-amber-100 text-amber-700",
    PREPARING: "bg-blue-100 text-blue-700",
    READY: "bg-blue-100 text-blue-700",
    PICKED_UP: "bg-indigo-100 text-indigo-700",
    OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-700",
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

function fmtRelative(iso) {
    if (!iso) return "never";
    const d = new Date(iso).getTime();
    const diff = Date.now() - d;
    const days = Math.floor(diff / 86_400_000);
    if (days === 0) return "today";
    if (days === 1) return "1d ago";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
}

export default function AdminCustomers() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailTab, setDetailTab] = useState("orders");

    const load = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (statusFilter) params.set("status", statusFilter);
            const { data } = await api.get(`/admin/customers?${params}`);
            setRows(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load customers");
        } finally {
            setBusy(false);
        }
    };
    // Initial load — defer through a microtask so the synchronous
    // setBusy(true) inside load() doesn't trip the React
    // ``set-state-in-effect`` lint rule. We've manually verified the
    // empty deps array; this is a strict-mode-safe one-shot effect.
    useEffect(() => {
        Promise.resolve().then(load);
    }, []);     // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced server-side search — the 350 ms setTimeout already
    // moves the setBusy out of the synchronous effect body so the
    // ``set-state-in-effect`` rule is satisfied.
    useEffect(() => {
        const t = setTimeout(() => { load(); }, 350);
        return () => clearTimeout(t);
    }, [q, statusFilter]);     // eslint-disable-line react-hooks/exhaustive-deps

    // Headline KPIs — total customers, blocked, total LTV captured.
    // Computed client-side from the loaded slice (caller controls
    // ``limit``) so the numbers always match what's visible.
    const kpis = useMemo(() => {
        const blocked = rows.filter((r) => r.status === "BLOCKED").length;
        const totalLtv = rows.reduce((s, r) => s + (r.total_spent || 0), 0);
        const everOrdered = rows.filter((r) => r.total_orders > 0).length;
        return { total: rows.length, blocked, totalLtv, everOrdered };
    }, [rows]);

    const openDetail = async (id) => {
        setSelectedId(id);
        setDetail(null);
        setDetailLoading(true);
        setDetailTab("orders");
        try {
            const { data } = await api.get(`/admin/customers/${id}`);
            setDetail(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load customer");
            setSelectedId(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const toggleStatus = async (cust) => {
        const next = cust.status === "BLOCKED" ? "ACTIVE" : "BLOCKED";
        try {
            const { data } = await api.patch(`/admin/customers/${cust.id}/status`, { status: next });
            // Optimistically update the row + drawer.
            setRows((rs) => rs.map((r) => (r.id === cust.id ? { ...r, status: data.status } : r)));
            if (detail?.id === cust.id) setDetail((d) => ({ ...d, status: data.status }));
            toast.success(next === "BLOCKED" ? "Customer blocked" : "Customer unblocked");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Status change failed");
        }
    };

    const [healing, setHealing] = useState(false);
    const healDuplicates = async () => {
        if (!detail) return;
        // Short confirm — duplicates merge is destructive (soft-deletes
        // older rows) so we want the admin to confirm intent.
        const ok = window.confirm(
            `Merge any duplicate accounts that share ${detail.mobile_number}?\n\n` +
            "Orders, coins, wallet and addresses will move onto this customer record. " +
            "Old duplicate rows are soft-deleted (status=MERGED) — nothing is lost.\n\n" +
            "This only affects accounts in your own tenant.",
        );
        if (!ok) return;
        setHealing(true);
        try {
            const { data } = await api.post(`/admin/customers/${detail.id}/heal-duplicates`);
            if (!data.merged) {
                toast.info(data.message || "No duplicates found for this customer.");
                return;
            }
            const lines = [
                `Merged ${data.merged_duplicate_user_ids?.length || 0} duplicate row(s).`,
                `Orders re-keyed: ${data.orders_rekeyed}`,
                `Coin balance: ${data.balance}`,
                `Wallet balance: ₹${data.wallet_balance}`,
            ];
            toast.success(lines.join(" · "));
            // Reload the customer drawer + the row list so updated balances render.
            await openDetail(data.canonical_user_id || detail.id);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Heal failed");
        } finally {
            setHealing(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-7rem)]">
            <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h1 className="text-2xl font-bold">Customers</h1>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            All customers in your tenant — lifetime value, recent orders, wallet & coins, block control.
                        </p>
                    </div>
                    <button onClick={load} disabled={busy} className="rounded-xl bg-card border border-border px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="cust-refresh">
                        <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <Kpi label="Total" value={kpis.total} data-testid="kpi-total" />
                    <Kpi label="Ever ordered" value={kpis.everOrdered} sub={`${kpis.total - kpis.everOrdered} new`} />
                    <Kpi label="Total LTV" value={inr(kpis.totalLtv)} />
                    <Kpi label="Blocked" value={kpis.blocked} tone={kpis.blocked > 0 ? "danger" : "default"} />
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="flex-1 relative min-w-[220px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            data-testid="cust-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name, mobile, email…"
                            className="w-full bg-card border border-border rounded-xl pl-9 pr-3 py-2 text-sm outline-none focus:border-primary"
                        />
                    </div>
                    <select
                        data-testid="cust-status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-card border border-border rounded-xl px-3 py-2 text-sm outline-none"
                    >
                        <option value="">All statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="BLOCKED">Blocked</option>
                    </select>
                </div>

                <div className="flex-1 overflow-auto bg-card rounded-2xl border border-border">
                    {/* Mobile + tablet portrait (<lg): stacked card list */}
                    <div className="lg:hidden">
                        {busy && rows.length === 0 ? (
                            <div className="py-10 text-center text-muted-foreground"><Loader2 size={18} className="inline animate-spin" /></div>
                        ) : rows.length === 0 ? (
                            <div className="py-10 text-center text-muted-foreground text-sm">No customers match.</div>
                        ) : rows.map((c) => (
                            <div
                                key={c.id}
                                data-testid={`cust-card-${c.id}`}
                                onClick={() => openDetail(c.id)}
                                className={`border-t border-border first:border-t-0 px-4 py-3 cursor-pointer active:bg-muted/40 ${selectedId === c.id ? "bg-primary/5" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-semibold text-sm truncate">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{c.mobile_number}</div>
                                        {c.email && <div className="text-[10px] text-muted-foreground truncate">{c.email}</div>}
                                    </div>
                                    <span className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${STATUS_COLOURS[c.status] || "bg-muted"}`}>
                                        {c.status}
                                    </span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                    <div className="text-muted-foreground"><span className="uppercase tracking-wider font-bold text-[9px]">Orders</span> · <span className="font-semibold text-foreground">{c.total_orders}</span></div>
                                    <div className="text-muted-foreground"><span className="uppercase tracking-wider font-bold text-[9px]">LTV</span> · <span className="font-semibold text-foreground">{inr(c.total_spent)}</span></div>
                                    <div className="text-muted-foreground"><span className="uppercase tracking-wider font-bold text-[9px]">Wallet</span> · <span className="font-semibold text-foreground">{inr(c.wallet_balance)}</span></div>
                                    <div className="text-amber-700 font-semibold inline-flex items-center gap-1"><CoinsIcon size={10} /> {c.coins_balance}</div>
                                    <div className="text-muted-foreground col-span-2">
                                        <span className="uppercase tracking-wider font-bold text-[9px]">Last</span> · {fmtRelative(c.last_order_at)}
                                        {c.last_order_status && (
                                            <span className={`ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${ORDER_STATUS_COLOURS[c.last_order_status] || "bg-muted text-muted-foreground"}`}>
                                                {c.last_order_status.replace("_", " ")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 flex justify-end">
                                    <button
                                        data-testid={`cust-block-card-${c.id}`}
                                        onClick={(e) => { e.stopPropagation(); toggleStatus(c); }}
                                        className={`text-xs font-semibold rounded-lg px-3 py-2.5 min-h-[44px] inline-flex items-center gap-1 ${c.status === "BLOCKED" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                                    >
                                        {c.status === "BLOCKED" ? <><ShieldCheck size={14} /> Unblock</> : <><ShieldOff size={14} /> Block</>}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop (lg+): classic table */}
                    <table className="hidden lg:table w-full text-sm">
                        <thead className="bg-muted/60 text-left sticky top-0 z-10">
                            <tr>
                                <Th>Customer</Th>
                                <Th>Orders</Th>
                                <Th>LTV</Th>
                                <Th>AOV</Th>
                                <Th>Wallet / Coins</Th>
                                <Th>Last order</Th>
                                <Th>Status</Th>
                                {/* iter146 — sticky (both axes: thead is already
                                    sticky top-0, this cell adds sticky right-0)
                                    so Block/Unblock stays reachable without
                                    scrolling all the way right. */}
                                <Th className="text-right sticky right-0 z-20 bg-muted/60 border-l border-border">Action</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {busy && rows.length === 0 ? (
                                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><Loader2 size={18} className="inline animate-spin" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No customers match.</td></tr>
                            ) : rows.map((c) => (
                                <tr
                                    key={c.id}
                                    onClick={() => openDetail(c.id)}
                                    data-testid={`cust-row-${c.id}`}
                                    className={`group border-t border-border cursor-pointer hover:bg-muted/40 ${selectedId === c.id ? "bg-primary/5" : ""}`}
                                >
                                    <Td>
                                        <div className="font-semibold leading-tight">{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}</div>
                                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{c.mobile_number}</div>
                                        {c.email && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{c.email}</div>}
                                    </Td>
                                    <Td><span className="font-semibold">{c.total_orders}</span></Td>
                                    <Td><span className="font-semibold">{inr(c.total_spent)}</span></Td>
                                    <Td className="text-muted-foreground">{c.total_orders ? inr(c.avg_order_value) : "—"}</Td>
                                    <Td>
                                        <div className="text-xs leading-tight">
                                            <div className="font-semibold">{inr(c.wallet_balance)}</div>
                                            <div className="text-[10px] text-amber-700 font-semibold inline-flex items-center gap-0.5"><CoinsIcon size={9} /> {c.coins_balance}</div>
                                        </div>
                                    </Td>
                                    <Td>
                                        <div className="text-xs leading-tight">
                                            <div className="font-mono text-[11px]">{fmtRelative(c.last_order_at)}</div>
                                            {c.last_order_status && (
                                                <span className={`mt-0.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${ORDER_STATUS_COLOURS[c.last_order_status] || "bg-muted text-muted-foreground"}`}>
                                                    {c.last_order_status.replace("_", " ")}
                                                </span>
                                            )}
                                        </div>
                                    </Td>
                                    <Td>
                                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${STATUS_COLOURS[c.status] || "bg-muted"}`}>
                                            {c.status}
                                        </span>
                                    </Td>
                                    <Td className={`text-right sticky right-0 border-l border-border group-hover:bg-muted/40 ${selectedId === c.id ? "bg-primary/5" : "bg-card"}`}>
                                        <button
                                            data-testid={`cust-block-${c.id}`}
                                            onClick={(e) => { e.stopPropagation(); toggleStatus(c); }}
                                            className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 ${c.status === "BLOCKED" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}
                                        >
                                            {c.status === "BLOCKED" ? <><ShieldCheck size={12} /> Unblock</> : <><ShieldOff size={12} /> Block</>}
                                        </button>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedId && (
                <CustomerDrawer
                    detail={detail}
                    loading={detailLoading}
                    activeTab={detailTab}
                    onTab={setDetailTab}
                    onClose={() => { setSelectedId(null); setDetail(null); }}
                    onToggle={() => detail && toggleStatus(detail)}
                    onHeal={healDuplicates}
                    healing={healing}
                />
            )}
        </div>
    );
}

function Kpi({ label, value, sub, tone = "default", "data-testid": testId }) {
    return (
        <div className={`rounded-2xl border p-3 ${tone === "danger" ? "bg-red-50 border-red-200" : "bg-card border-border"}`} data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
            <div className="text-xl font-extrabold mt-0.5">{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

function CustomerDrawer({ detail, loading, activeTab, onTab, onClose, onToggle, onHeal, healing }) {
    return (
        <div className="w-full lg:w-[420px] shrink-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden" data-testid="cust-drawer">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                <div className="font-bold">Customer detail</div>
                <button onClick={onClose} className="rounded-full p-1 hover:bg-muted" data-testid="cust-drawer-close"><X size={16} /></button>
            </div>
            {loading || !detail ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>
            ) : (
                <>
                    <div className="p-4 border-b border-border space-y-2">
                        <div className="text-lg font-extrabold">{`${detail.first_name || ""} ${detail.last_name || ""}`.trim() || "—"}</div>
                        <div className="text-xs text-muted-foreground space-y-1">
                            <div className="inline-flex items-center gap-1.5"><Phone size={11} /> <span className="font-mono">{detail.mobile_number}</span></div>
                            {detail.email && <div className="inline-flex items-center gap-1.5 break-all"><Mail size={11} /> {detail.email}</div>}
                            <div className="inline-flex items-center gap-1.5"><Calendar size={11} /> Joined {fmtDate(detail.created_at)}</div>
                        </div>
                        <div className="flex items-center gap-1.5 pt-1">
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${STATUS_COLOURS[detail.status] || "bg-muted"}`}>{detail.status}</span>
                            <button
                                onClick={onHeal}
                                disabled={healing}
                                data-testid="cust-drawer-heal-duplicates"
                                title="Merge duplicate accounts that share this mobile (within this tenant only)"
                                className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1 bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                            >
                                {healing ? <Loader2 size={11} className="animate-spin" /> : <Merge size={11} />}
                                Heal duplicates
                            </button>
                            <button
                                onClick={onToggle}
                                data-testid="cust-drawer-toggle-status"
                                className={`text-xs font-semibold rounded-lg px-2.5 py-1 ${detail.status === "BLOCKED" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                            >
                                {detail.status === "BLOCKED" ? "Unblock" : "Block"}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2">
                            <Stat icon={ShoppingBag} label="Orders" value={detail.metrics.total_orders} />
                            <Stat icon={IndianRupee} label="LTV" value={inr(detail.metrics.total_spent)} />
                            <Stat icon={WalletIcon} label="Wallet" value={inr(detail.wallet.balance)} />
                            <Stat icon={CoinsIcon} label="Coins" value={detail.coins.balance} />
                        </div>
                    </div>
                    <div className="flex border-b border-border bg-muted/20 text-xs font-semibold">
                        {["orders", "addresses", "wallet", "coins"].map((t) => (
                            <button
                                key={t}
                                data-testid={`cust-tab-${t}`}
                                onClick={() => onTab(t)}
                                className={`flex-1 py-2.5 capitalize ${activeTab === t ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`}
                            >{t}</button>
                        ))}
                    </div>
                    <div className="flex-1 overflow-auto p-3 space-y-2 text-xs">
                        {activeTab === "orders" && (detail.recent_orders.length === 0 ? <Empty msg="No orders yet." /> :
                            detail.recent_orders.map((o) => (
                                <div key={o.id} className="rounded-xl border border-border p-2.5 bg-muted/20">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-mono text-[10px] text-muted-foreground">{o.order_unique_id}</div>
                                            <div className="font-semibold truncate max-w-[200px]">{o.restaurant_name || "—"}</div>
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${ORDER_STATUS_COLOURS[o.status] || "bg-muted"}`}>{o.status.replace("_", " ")}</span>
                                    </div>
                                    <div className="flex items-center justify-between mt-1.5 text-[11px]">
                                        <span className="text-muted-foreground">{o.items_count} item{o.items_count !== 1 ? "s" : ""}: {o.items_preview.join(", ")}</span>
                                        <span className="font-bold">{inr(o.total)}</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(o.created_at)} · {o.payment_method}</div>
                                </div>
                            )))}
                        {activeTab === "addresses" && (detail.addresses.length === 0 ? <Empty msg="No saved addresses." /> :
                            detail.addresses.map((a) => (
                                <div key={a.id} className="rounded-xl border border-border p-2.5 bg-muted/20">
                                    <div className="flex items-start gap-1.5">
                                        <MapPin size={12} className="text-primary mt-0.5 shrink-0" />
                                        <div className="flex-1">
                                            <div className="font-bold text-[11px]">{a.label}{a.is_default && <span className="ml-1.5 text-[9px] bg-primary/15 text-primary px-1 rounded">DEFAULT</span>}</div>
                                            <div>{[a.line1, a.line2, a.city, a.pincode].filter(Boolean).join(", ")}</div>
                                            {a.landmark && <div className="text-[10px] text-muted-foreground">Landmark: {a.landmark}</div>}
                                        </div>
                                    </div>
                                </div>
                            )))}
                        {activeTab === "wallet" && (detail.wallet.transactions.length === 0 ? <Empty msg="No wallet activity." /> :
                            detail.wallet.transactions.map((t) => <LedgerRow key={t.id} t={t} />))}
                        {activeTab === "coins" && (detail.coins.transactions.length === 0 ? <Empty msg="No coin activity." /> :
                            detail.coins.transactions.map((t) => <CoinsRow key={t.id} t={t} />))}
                    </div>
                </>
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="rounded-xl bg-muted/30 p-2">
            <div className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground inline-flex items-center gap-1"><Icon size={9} /> {label}</div>
            <div className="text-sm font-extrabold mt-0.5">{value}</div>
        </div>
    );
}

function Empty({ msg }) { return <div className="py-8 text-center text-muted-foreground">{msg}</div>; }

function LedgerRow({ t }) {
    const positive = (t.kind || "").toUpperCase() === "CREDIT" || (t.delta || 0) > 0;
    return (
        <div className="rounded-xl border border-border p-2 bg-muted/20 flex items-start justify-between gap-2">
            <div className="min-w-0">
                <div className="font-semibold text-[11px]">{t.reason || t.kind || "Wallet txn"}</div>
                <div className="text-[10px] text-muted-foreground">{fmtDate(t.created_at)}</div>
            </div>
            <div className={`text-[11px] font-extrabold ${positive ? "text-emerald-700" : "text-red-700"}`}>
                {positive ? "+" : "−"}{inr(Math.abs(t.delta || (t.new_balance - t.previous_balance) || 0))}
            </div>
        </div>
    );
}

function CoinsRow({ t }) {
    const positive = (t.delta || 0) >= 0;
    return (
        <div className="rounded-xl border border-border p-2 bg-muted/20 flex items-start justify-between gap-2">
            <div className="min-w-0">
                <div className="font-semibold text-[11px]">{(t.type || "").replace(/_/g, " ")}</div>
                {t.note && <div className="text-[10px] text-muted-foreground truncate">{t.note}</div>}
                <div className="text-[10px] text-muted-foreground">{fmtDate(t.created_at)}</div>
            </div>
            <div className={`text-[11px] font-extrabold inline-flex items-center gap-0.5 ${positive ? "text-amber-700" : "text-red-700"}`}>
                <CoinsIcon size={10} /> {positive ? "+" : ""}{t.delta}
            </div>
        </div>
    );
}
